import type { AxiosInstance } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "@/contexts/AxiosContext";
import type { CharacterDetailsResponse, MutableEntityListItem } from "@/types/project";
import { getErrorMessage, type MutableEntityReorderBody, throwLockAwareError } from "@/api/apiClient";

/** Request body for character POST — same as GET payload but never includes `lock` (see rest-api/readme.md). */
export type CharacterUpdateBody = Omit<CharacterDetailsResponse, "lock">;

type ProjectCharacterIds = Readonly<{ projectId: string; characterId: string }>;

// --- Character API (GET list, GET detail + lock, POST update) — rest-api/readme.md ---

const fetchCharacterList = async (axiosClient: AxiosInstance, projectId: string): Promise<readonly MutableEntityListItem[]> => {
  try {
    const { data } = await axiosClient.get<readonly MutableEntityListItem[]>(`/${encodeURIComponent(projectId)}/character`);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when listing characters."));
  }
};

const fetchCharacter = async (axiosClient: AxiosInstance, projectId: string, characterId: string): Promise<CharacterDetailsResponse> => {
  try {
    const { data } = await axiosClient.get<CharacterDetailsResponse>(`/${encodeURIComponent(projectId)}/character/${encodeURIComponent(characterId)}`);
    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when loading character."));
  }
};

const updateCharacter = async (
  axiosClient: AxiosInstance,
  projectId: string,
  characterId: string,
  body: CharacterUpdateBody
): Promise<{ status: "ok" }> => {
  try {
    const { data } = await axiosClient.post<{ status: "ok" }>(`/${encodeURIComponent(projectId)}/character/${encodeURIComponent(characterId)}`, body);
    return data;
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when updating character.");
  }
};

const deleteCharacter = async (axiosClient: AxiosInstance, projectId: string, characterId: string): Promise<{ status: "ok" }> => {
  try {
    const { data } = await axiosClient.delete<{ status: "ok" }>(`/${encodeURIComponent(projectId)}/character/${encodeURIComponent(characterId)}`);
    return data;
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when deleting character.");
  }
};

const reorderCharacter = async (
  axiosClient: AxiosInstance,
  projectId: string,
  characterId: string,
  body: MutableEntityReorderBody
): Promise<{ status: "ok" }> => {
  try {
    const { data } = await axiosClient.patch<{ status: "ok" }>(`/${encodeURIComponent(projectId)}/character/${encodeURIComponent(characterId)}`, body);
    return data;
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when reordering character.");
  }
};

// ===== React Query hooks =====

export const useCharacterList = (projectId: string | null) => {
  const axiosClient = useAxios();
  return useQuery({
    queryKey: ["characters", projectId],
    queryFn: async () => fetchCharacterList(axiosClient, projectId!),
    enabled: typeof projectId === "string" && projectId.length > 0,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

export const useCharacter = (projectId: string | null, characterId: string | null) => {
  const axiosClient = useAxios();
  return useQuery({
    queryKey: ["character", projectId, characterId],
    queryFn: async () => fetchCharacter(axiosClient, projectId!, characterId!),
    enabled: typeof projectId === "string" && projectId.length > 0 && typeof characterId === "string" && characterId.length > 0,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

export const useUpdateCharacter = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(vars: { readonly projectId: string; readonly characterId: string; readonly body: CharacterUpdateBody }) {
      return updateCharacter(axiosClient, vars.projectId, vars.characterId, vars.body);
    },
    onSuccess(_, vars) {
      void queryClient.invalidateQueries({ queryKey: ["character", vars.projectId, vars.characterId] });
      void queryClient.invalidateQueries({ queryKey: ["characters", vars.projectId] });
      void queryClient.invalidateQueries({ queryKey: ["project", vars.projectId] });
    }
  });
};

export const useDeleteCharacter = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: ProjectCharacterIds) => deleteCharacter(axiosClient, vars.projectId, vars.characterId),
    onSuccess(_, vars) {
      void queryClient.removeQueries({ queryKey: ["character", vars.projectId, vars.characterId] });
      void queryClient.invalidateQueries({ queryKey: ["characters", vars.projectId] });
      void queryClient.invalidateQueries({ queryKey: ["project", vars.projectId] });
    }
  });
};

export const useReorderCharacter = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(vars: { readonly projectId: string; readonly characterId: string; readonly body: MutableEntityReorderBody }) {
      return reorderCharacter(axiosClient, vars.projectId, vars.characterId, vars.body);
    },
    onSuccess(_, vars) {
      void queryClient.invalidateQueries({ queryKey: ["character", vars.projectId, vars.characterId] });
      void queryClient.invalidateQueries({ queryKey: ["characters", vars.projectId] });
      void queryClient.invalidateQueries({ queryKey: ["project", vars.projectId] });
    }
  });
};
